import { supabase } from '../../../lib/supabase';
import { generateContentWithCache } from '../../../lib/geminiService';
import { getLocalDateString } from '../utils/calendarUtils';
import { compressImage } from '../../../utils/imageUtils';
import { parseAIJSON } from '../../../utils/aiUtils';
import { isCbnaZeroTaxNcm, calculateItemTaxCredits } from '@r-max/shared';
import type { 
  FinancialDraft, 
  ServiceDraftItem,
  ClientDraft,
  InventoryDraft,
  AppointmentDraft,
  WorkOrderDraft,
  RecipeDraft
} from '../utils/commandBridge';

export interface AgenticCommandResponse {
  success: boolean;
  userTier?: 'free' | 'pro' | 'especialista' | 'empresa';
  type: 
    | 'text' 
    | 'proposal' 
    | 'read_result' 
    | 'tier_required' 
    | 'navigation' 
    | 'financial_draft' 
    | 'services_draft'
    | 'client_draft'
    | 'inventory_draft'
    | 'appointment_draft'
    | 'work_order_draft'
    | 'recipe_draft';
  content?: string;
  toolName?: string;
  data?: any;
  proposal?: {
    actionId: string;
    toolName: string;
    description: string;
    parameters: Record<string, any>;
    diffSummary?: {
      field: string;
      currentValue?: any;
      proposedValue: any;
    }[];
    items?: Array<{
      name: string;
      price: number;
      unit: string;
      quantity?: number;
      category?: string;
    }>;
    totalValue?: number;
  };
  navigationTarget?: {
    view: string;
    label: string;
  };
  message?: string;
  error?: string;
  financialDraft?: FinancialDraft;
  servicesDraft?: ServiceDraftItem[];
  clientDraft?: ClientDraft;
  inventoryDraft?: InventoryDraft;
  appointmentDraft?: AppointmentDraft;
  workOrderDraft?: WorkOrderDraft;
  recipeDraft?: RecipeDraft;
}

/** Converte arquivo (imagem ou PDF) em parte legível pelo Gemini 3.5 Flash Lite. */
export async function buildFileAiPart(file: File): Promise<{ inlineData: { data: string; mimeType: string } }> {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  if (isPdf) {
    if (file.size > 4.5 * 1024 * 1024) {
      throw new Error('O arquivo PDF excede o limite de 4.5MB. Envie um arquivo menor ou uma foto.');
    }
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve((e.target?.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return { inlineData: { data: base64, mimeType: 'application/pdf' } };
  } else {
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('A imagem excede 5MB. Tire uma foto mais próxima ou reduza a resolução.');
    }
    const dataUrl = await compressImage(file, { purpose: 'ocr' });
    return { inlineData: { data: dataUrl.split(',')[1], mimeType: 'image/jpeg' } };
  }
}

/* ========================================================================= */
/* 1. PONTE FINANCEIRA (CONTAS A PAGAR & RECEBER)                           */
/* ========================================================================= */

export function looksLikeFinancialIntent(text: string): boolean {
  const str = text || '';
  // Se mencionar lote/estoque/validade sem nenhum termo financeiro explícito, não é financeiro
  if (/\b(lote|estoque|validade)\b/i.test(str) && !/\b(pagar|receber|cobrar|boleto|duplicata|fatura)\b/i.test(str)) {
    return false;
  }
  return /\b(contas?\s+a\s+(pagar|receber)|a\s+pagar|a\s+receber|boleto|duplicata|vencimento|vence(\s+dia|\s+amanh|\s+em\s+\d)?|pagar\s+\S+|receber\s+(de|do|da)\s+\S+|cobrar\s+\S+)\b/i.test(str);
}

export async function prepareFinancialDraft(
  prompt: string,
  file?: File | null
): Promise<AgenticCommandResponse | null> {
  const today = getLocalDateString();
  const instruction = `Você interpreta pedidos de contas a pagar e a receber do R-MAX.
Data de hoje: ${today}.
Retorne EXCLUSIVAMENTE um JSON:
{
  "isTitulo": true | false,
  "tipo": "DESPESA" | "RECEITA",
  "descricao": "texto curto",
  "valor": 0.00,
  "dataVencimento": "YYYY-MM-DD ou vazio se não informado",
  "pessoaNome": "fornecedor (DESPESA) ou cliente (RECEITA), ou vazio",
  "numeroDocumento": "número do boleto/nota ou vazio",
  "observacoes": "vazio ou detalhe relevante"
}
Regras:
1. "isTitulo" é false se for consulta, dúvida, lançamento de compra já realizada ou qualquer outro assunto.
2. "pagar", boleto, conta de fornecedor = DESPESA. "receber", cobrar cliente = RECEITA.
3. Resolva datas relativas ("dia 10", "amanhã") a partir da data de hoje, escolhendo a próxima ocorrência futura.
4. NUNCA invente valor ou data. Se não constar, use 0 ou vazio.
5. Retorne apenas o JSON.`;

  const parts: any[] = [{ text: `${instruction}\n\nPedido do operador: "${prompt || '(somente anexo)'}"` }];

  if (file) {
    const filePart = await buildFileAiPart(file);
    parts.push(filePart);
  }

  const aiRes = await generateContentWithCache({
    model: 'gemini-3.5-flash-lite',
    contents: [{ parts }],
    config: { generationConfig: { responseMimeType: 'application/json' }, temperature: 0.1 },
    skipCache: true
  });

  const parsed = parseAIJSON<any>(aiRes.text, null);
  if (!parsed || parsed.isTitulo !== true) return null;

  const valor = Number(parsed.valor || 0);
  const venc = /^\d{4}-\d{2}-\d{2}$/.test(String(parsed.dataVencimento || '')) ? String(parsed.dataVencimento) : undefined;
  const draft: FinancialDraft = {
    tipo: parsed.tipo === 'RECEITA' ? 'RECEITA' : 'DESPESA',
    descricao: String(parsed.descricao || '').trim() || (parsed.tipo === 'RECEITA' ? 'Conta a receber' : 'Conta a pagar'),
    valorOriginal: Number.isFinite(valor) && valor > 0 ? valor : 0,
    dataVencimento: venc,
    pessoaNome: String(parsed.pessoaNome || '').trim() || undefined,
    numeroDocumento: String(parsed.numeroDocumento || '').trim() || undefined,
    observacoes: String(parsed.observacoes || '').trim() || undefined
  };

  // Conta a receber sem valor informado: sugere o valor do último orçamento aprovado do cliente
  if (draft.tipo === 'RECEITA' && draft.valorOriginal === 0 && draft.pessoaNome) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        const { data: quote } = await supabase
          .from('quotes')
          .select('final_price, status, created_at, client_name')
          .eq('user_id', user.id)
          .ilike('client_name', `%${draft.pessoaNome}%`)
          .in('status', ['approved', 'completed'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (quote && Number(quote.final_price) > 0) {
          draft.valorOriginal = Number(quote.final_price);
          draft.observacoes = [draft.observacoes, `Valor sugerido pelo último orçamento aprovado de ${quote.client_name}. Confira antes de gravar.`]
            .filter(Boolean)
            .join(' ');
        }
      }
    } catch (lookupErr) {
      console.warn('[AgenticCommandService] Consulta de orçamento aprovado indisponível:', lookupErr);
    }
  }

  return {
    success: true,
    type: 'financial_draft',
    toolName: 'financeiro_preparar_titulo',
    financialDraft: draft,
    message: 'Rascunho preparado. Confira os dados na tela de Contas a Pagar e Receber antes de gravar.'
  };
}

/* ========================================================================= */
/* 2. PONTE DE SERVIÇOS (CATÁLOGO DE SERVIÇOS)                              */
/* ========================================================================= */

export function looksLikeServicesIntent(text: string): boolean {
  if (/ordem(ns)?\s+de\s+servi/i.test(text || '')) return false;
  return /(tabela|lista)\s+de\s+pre[cç]os?|cat[aá]logo\s+de\s+servi[cç]os|(cadastr\w+|inclu\w+|adicion\w+)\s+(o\s+|um\s+|novo\s+)?servi[cç]os?/i.test(text || '');
}

export async function prepareServicesDraft(
  prompt: string,
  file?: File | null
): Promise<AgenticCommandResponse | null> {
  const instruction = `Você extrai serviços e preços para o catálogo do R-MAX.
Retorne EXCLUSIVAMENTE um JSON:
{
  "services": [
    { "name": "Nome do serviço", "type": "fixed" | "hourly", "value": 0.00, "description": "opcional" }
  ]
}
Regras:
1. "hourly" somente se o serviço for cobrado por hora; valor fechado, pacote ou diária é "fixed".
2. "value" é número decimal. Se não constar, use 0. NUNCA invente valores.
3. Ignore produtos e insumos de revenda; liste apenas serviços prestados.
4. Máximo de 40 serviços. Retorne apenas o JSON.`;

  const parts: any[] = [{ text: `${instruction}\n\nPedido do operador: "${prompt || '(somente anexo)'}"` }];

  if (file) {
    const filePart = await buildFileAiPart(file);
    parts.push(filePart);
  }

  const aiRes = await generateContentWithCache({
    model: 'gemini-3.5-flash-lite',
    contents: [{ parts }],
    config: { generationConfig: { responseMimeType: 'application/json' }, temperature: 0.1 },
    skipCache: true
  });

  const parsed = parseAIJSON<{ services?: any[] }>(aiRes.text, { services: [] });
  const list = Array.isArray(parsed?.services) ? parsed!.services!.slice(0, 40) : [];
  const items: ServiceDraftItem[] = list
    .map((s: any) => {
      const value = Number(s?.value || 0);
      return {
        name: String(s?.name || '').trim(),
        type: s?.type === 'hourly' ? 'hourly' : 'fixed',
        value: Number.isFinite(value) && value > 0 ? value : 0,
        description: s?.description ? String(s.description).trim() : undefined
      } as ServiceDraftItem;
    })
    .filter((s) => s.name.length > 0);

  if (items.length === 0) return null;

  return {
    success: true,
    type: 'services_draft',
    toolName: 'servicos_preparar_lote',
    servicesDraft: items,
    message: 'Serviços identificados. Confira nome, tipo de cobrança e valor no Catálogo de Serviços antes de gravar.'
  };
}

/* ========================================================================= */
/* 3. PONTE DE CLIENTES (CRM DE CLIENTES)                                   */
/* ========================================================================= */

export function looksLikeClientIntent(text: string): boolean {
  if (/(contas?\s+a\s+receber|cobrar\s+cliente|agend|ordem\s+de\s+servi)/i.test(text || '')) return false;
  return /\b(cadastr\w+|inclu\w+|adicion\w+|novo|salvar)\s+(o\s+|um\s+)?(cliente|contato)\b|\bcart[aã]o\s+de\s+visita\b/i.test(text || '');
}

export async function prepareClientDraft(
  prompt: string,
  file?: File | null
): Promise<AgenticCommandResponse | null> {
  const instruction = `Você extrai dados cadastrais de cliente para o CRM do R-MAX.
Retorne EXCLUSIVAMENTE um JSON:
{
  "isClient": true | false,
  "name": "Nome completo ou Razão Social",
  "phone": "Telefone ou WhatsApp ou vazio",
  "email": "Email se informado ou vazio",
  "document": "CPF ou CNPJ se informado ou vazio",
  "address": "Endereço completo ou vazio",
  "notes": "Observações relevantes ou vazio"
}
Regras:
1. "isClient" é false se o pedido não for sobre cadastrar ou registrar um cliente/contato.
2. Nome é obrigatório. Se não houver nome claro de pessoa ou empresa, isClient deve ser false.
3. NUNCA invente telefones, emails, documentos ou endereços inexistentes.
4. Retorne apenas o JSON.`;

  const parts: any[] = [{ text: `${instruction}\n\nPedido do operador: "${prompt || '(somente anexo)'}"` }];

  if (file) {
    const filePart = await buildFileAiPart(file);
    parts.push(filePart);
  }

  const aiRes = await generateContentWithCache({
    model: 'gemini-3.5-flash-lite',
    contents: [{ parts }],
    config: { generationConfig: { responseMimeType: 'application/json' }, temperature: 0.1 },
    skipCache: true
  });

  const parsed = parseAIJSON<any>(aiRes.text, null);
  if (!parsed || parsed.isClient !== true || !parsed.name?.trim()) return null;

  const draft: ClientDraft = {
    name: String(parsed.name).trim(),
    phone: parsed.phone ? String(parsed.phone).trim() : undefined,
    email: parsed.email ? String(parsed.email).trim() : undefined,
    document: parsed.document ? String(parsed.document).trim() : undefined,
    address: parsed.address ? String(parsed.address).trim() : undefined,
    notes: parsed.notes ? String(parsed.notes).trim() : undefined
  };

  return {
    success: true,
    type: 'client_draft',
    toolName: 'crm_preparar_cliente',
    clientDraft: draft,
    message: 'Cliente identificado. Confira os dados no CRM de Clientes antes de gravar.'
  };
}

/* ========================================================================= */
/* 4. PONTE DE ESTOQUE (ESTOQUE & LOTES COM VALIDADE)                        */
/* ========================================================================= */

export function looksLikeInventoryIntent(text: string): boolean {
  if (looksLikeFinancialIntent(text)) return false;
  return /\b(lote|validade|vence\s+em|saldo\s+de\s+estoque|entrada\s+de\s+estoque|ajuste\s+de\s+estoque|dar\s+entrada|dar\s+baixa|baixa\s+de\s+estoque|baixa\s+no\s+estoque|abrir\s+pote|abertura\s+de\s+pote|repor\s+estoque|avaria|descarte|perda\s+de\s+estoque)\b/i.test(text || '');
}

export async function prepareInventoryDraft(
  prompt: string,
  file?: File | null
): Promise<AgenticCommandResponse | null> {
  const today = getLocalDateString();
  const instruction = `Você interpreta movimentações de estoque, controle de lotes, produtos e validade para o R-MAX.
Data de hoje: ${today}.
Retorne EXCLUSIVAMENTE um JSON:
{
  "isInventory": true | false,
  "materialName": "Nome do produto ou insumo",
  "supplierName": "Nome do fornecedor ou vazio se não houver",
  "barcode": "Código de barras, EAN, GTIN ou QR code se houver ou vazio",
  "stockControlMode": "pot_open" | "unit_sale" | vazio,
  "batchCode": "Código do lote ou vazio se não houver",
  "expirationDate": "YYYY-MM-DD da validade ou vazio",
  "quantity": 1.0,
  "unitCost": 0.00,
  "type": "entry_purchase" | "entry_adjustment" | "exit_pot_open" | "exit_freezer_restock" | "exit_loss_damaged" | "exit_loss_expired" | "exit_internal",
  "notes": "Observação relevante ou vazio"
}
Regras:
1. "isInventory" é false se for apenas compra simples de insumo sem lote/validade nem menção explícita a estoque.
2. Tipos de movimento:
   - "exit_pot_open": abertura de pote para buffet/balcão, começou a servir.
   - "exit_freezer_restock": reposição de picolé/bebida no freezer de vendas.
   - "exit_loss_damaged": avaria, quebra, descarte ou perda.
   - "exit_loss_expired": produto com validade vencida.
   - "exit_internal": consumo interno da equipe ou teste.
   - "entry_purchase": compra ou entrada de mercadoria.
   - "entry_adjustment": acerto/ajuste positivo de contagem de estoque.
3. Resolva datas de validade relativas (ex: "validade 12/26", "vence em maio") para o formato YYYY-MM-DD.
4. Se o item for sorvete de massa, açaí no buffet ou pote aberto, marque "stockControlMode": "pot_open". Se for picolé, água, refrigerante ou produto de revenda fechado, use "unit_sale".
5. NUNCA invente lote, fornecedor ou código inexistentes. Se não informado, deixe vazio.
6. Retorne apenas o JSON.`;

  const parts: any[] = [{ text: `${instruction}\n\nPedido do operador: "${prompt || '(somente anexo)'}"` }];

  if (file) {
    const filePart = await buildFileAiPart(file);
    parts.push(filePart);
  }

  const aiRes = await generateContentWithCache({
    model: 'gemini-3.5-flash-lite',
    contents: [{ parts }],
    config: { generationConfig: { responseMimeType: 'application/json' }, temperature: 0.1 },
    skipCache: true
  });

  const parsed = parseAIJSON<any>(aiRes.text, null);
  if (!parsed || parsed.isInventory !== true || !parsed.materialName?.trim()) return null;

  const qty = Number(parsed.quantity || 1);
  const cost = Number(parsed.unitCost || 0);
  const exp = /^\d{4}-\d{2}-\d{2}$/.test(String(parsed.expirationDate || '')) ? String(parsed.expirationDate) : undefined;

  const validMode = parsed.stockControlMode === 'pot_open' || parsed.stockControlMode === 'unit_sale' ? parsed.stockControlMode : undefined;

  const draft: InventoryDraft = {
    materialName: String(parsed.materialName).trim(),
    supplierName: parsed.supplierName ? String(parsed.supplierName).trim() : undefined,
    barcode: parsed.barcode ? String(parsed.barcode).trim() : undefined,
    stockControlMode: validMode,
    batchCode: parsed.batchCode ? String(parsed.batchCode).trim() : undefined,
    expirationDate: exp,
    quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
    unitCost: Number.isFinite(cost) && cost > 0 ? cost : undefined,
    type: ['entry_purchase', 'entry_adjustment', 'exit_pot_open', 'exit_freezer_restock', 'exit_loss_damaged', 'exit_loss_expired', 'exit_internal'].includes(parsed.type)
      ? parsed.type
      : 'entry_purchase',
    notes: parsed.notes ? String(parsed.notes).trim() : undefined
  };

  return {
    success: true,
    type: 'inventory_draft',
    toolName: 'estoque_preparar_movimentacao',
    inventoryDraft: draft,
    message: 'Movimentação de estoque identificada. Confira lote, quantidade e validade na tela de Estoque antes de gravar.'
  };
}

/* ========================================================================= */
/* 5. PONTE DE AGENDAMENTOS (AGENDA & ATENDIMENTOS)                         */
/* ========================================================================= */

export function looksLikeAppointmentIntent(text: string): boolean {
  return /\b(agend\w+|marcar\s+(hor[aá]rio|consulta|atendimento|sess[aã]o)|hor[aá]rio\s+para\s+\w+)\b/i.test(text || '');
}

export async function prepareAppointmentDraft(
  prompt: string,
  file?: File | null
): Promise<AgenticCommandResponse | null> {
  const today = getLocalDateString();
  const instruction = `Você interpreta agendamentos de horários e atendimentos para o R-MAX.
Data de hoje: ${today}.
Retorne EXCLUSIVAMENTE um JSON:
{
  "isAppointment": true | false,
  "clientName": "Nome do cliente",
  "clientPhone": "Telefone ou celular se houver ou vazio",
  "serviceName": "Procedimento, serviço ou tipo de consulta",
  "scheduledDate": "YYYY-MM-DD",
  "scheduledTime": "HH:mm",
  "price": 0.00,
  "durationMinutes": 30,
  "notes": "Observação relevante ou vazio"
}
Regras:
1. "isAppointment" é false se for apenas cadastro de cliente ou serviço sem intenção de agendar horário.
2. Resolva datas ("amanhã", "segunda-feira", "dia 15") a partir da data de hoje, escolhendo a próxima ocorrência.
3. Se não houver horário explícito, sugira "09:00".
4. Retorne apenas o JSON.`;

  const parts: any[] = [{ text: `${instruction}\n\nPedido do operador: "${prompt || '(somente anexo)'}"` }];

  if (file) {
    const filePart = await buildFileAiPart(file);
    parts.push(filePart);
  }

  const aiRes = await generateContentWithCache({
    model: 'gemini-3.5-flash-lite',
    contents: [{ parts }],
    config: { generationConfig: { responseMimeType: 'application/json' }, temperature: 0.1 },
    skipCache: true
  });

  const parsed = parseAIJSON<any>(aiRes.text, null);
  if (!parsed || parsed.isAppointment !== true) return null;

  const priceVal = Number(parsed.price || 0);
  const durVal = Number(parsed.durationMinutes || 30);
  const dateVal = /^\d{4}-\d{2}-\d{2}$/.test(String(parsed.scheduledDate || '')) ? String(parsed.scheduledDate) : today;
  const timeVal = /^\d{2}:\d{2}$/.test(String(parsed.scheduledTime || '')) ? String(parsed.scheduledTime) : '09:00';

  const draft: AppointmentDraft = {
    clientName: String(parsed.clientName || 'Cliente').trim(),
    clientPhone: parsed.clientPhone ? String(parsed.clientPhone).trim() : undefined,
    serviceName: String(parsed.serviceName || 'Atendimento').trim(),
    scheduledDate: dateVal,
    scheduledTime: timeVal,
    price: Number.isFinite(priceVal) && priceVal > 0 ? priceVal : undefined,
    durationMinutes: Number.isFinite(durVal) && durVal > 0 ? durVal : 30,
    notes: parsed.notes ? String(parsed.notes).trim() : undefined
  };

  return {
    success: true,
    type: 'appointment_draft',
    toolName: 'agenda_preparar_agendamento',
    appointmentDraft: draft,
    message: 'Agendamento identificado. Confira data, horário e profissional na Agenda antes de confirmar.'
  };
}

/* ========================================================================= */
/* 6. PONTE DE ORDENS DE SERVIÇO (ORDEM DE SERVIÇO / OS)                    */
/* ========================================================================= */

export function looksLikeWorkOrderIntent(text: string): boolean {
  if (looksLikeServicesIntent(text) && !/defeito|conserto|reparo|os\b/i.test(text || '')) return false;
  return /\b(ordem\s+de\s+servi[cç]o|abrir\s+os\b|nova\s+os\b|conserto|reparo|manuten[cç][aã]o|aparelho|defeito|laudo\s+t[eé]cnico)\b/i.test(text || '');
}

export async function prepareWorkOrderDraft(
  prompt: string,
  file?: File | null
): Promise<AgenticCommandResponse | null> {
  const instruction = `Você interpreta abertura de Ordens de Serviço (OS) e laudos para o R-MAX.
Retorne EXCLUSIVAMENTE um JSON:
{
  "isWorkOrder": true | false,
  "clientName": "Nome do cliente se informado ou vazio",
  "clientPhone": "Telefone se informado ou vazio",
  "clientAddress": "Endereço se informado ou vazio",
  "clientDocument": "CPF/CNPJ se informado ou vazio",
  "title": "Título resumido da OS (ex: Troca de tela iPhone 13, Manutenção ar condicionado Split, Revisão de freios)",
  "problemDescription": "Relato detalhado do problema ou serviço a executar",
  "deviceInfo": "Modelo do equipamento/veículo/aparelho se informado ou vazio",
  "notes": "Observações adicionais ou vazio"
}
Regras:
1. "isWorkOrder" é false se for compra de insumo ou agendamento simples sem reparo.
2. Título e relato do problema são fundamentais.
3. Retorne apenas o JSON.`;

  const parts: any[] = [{ text: `${instruction}\n\nPedido do operador: "${prompt || '(somente anexo)'}"` }];

  if (file) {
    const filePart = await buildFileAiPart(file);
    parts.push(filePart);
  }

  const aiRes = await generateContentWithCache({
    model: 'gemini-3.5-flash-lite',
    contents: [{ parts }],
    config: { generationConfig: { responseMimeType: 'application/json' }, temperature: 0.1 },
    skipCache: true
  });

  const parsed = parseAIJSON<any>(aiRes.text, null);
  if (!parsed || parsed.isWorkOrder !== true) return null;

  const draft: WorkOrderDraft = {
    clientName: String(parsed.clientName || '').trim(),
    clientPhone: parsed.clientPhone ? String(parsed.clientPhone).trim() : undefined,
    clientAddress: parsed.clientAddress ? String(parsed.clientAddress).trim() : undefined,
    clientDocument: parsed.clientDocument ? String(parsed.clientDocument).trim() : undefined,
    title: String(parsed.title || 'Ordem de Serviço').trim(),
    problemDescription: parsed.problemDescription ? String(parsed.problemDescription).trim() : undefined,
    deviceInfo: parsed.deviceInfo ? String(parsed.deviceInfo).trim() : undefined,
    notes: parsed.notes ? String(parsed.notes).trim() : undefined
  };

  return {
    success: true,
    type: 'work_order_draft',
    toolName: 'os_preparar_ordem_servico',
    workOrderDraft: draft,
    message: 'Ordem de Serviço preparada. Confira os detalhes na tela de OS antes de emitir.'
  };
}

/* ========================================================================= */
/* 7. PONTE DE RECEITAS & FICHAS TÉCNICAS                                    */
/* ========================================================================= */

export function looksLikeRecipeIntent(text: string): boolean {
  return /\b(ficha\s+t[eé]cnica|receita|fazer\s+uma\s+receita|rendimento|ingredientes?|composi[cç][aã]o\s+do\s+produto)\b/i.test(text || '');
}

export async function prepareRecipeDraft(
  prompt: string,
  file?: File | null
): Promise<AgenticCommandResponse | null> {
  const instruction = `Você extrai dados de receitas e fichas técnicas para o R-MAX.
Retorne EXCLUSIVAMENTE um JSON:
{
  "isRecipe": true | false,
  "name": "Nome da receita ou kit",
  "yieldAmount": 1,
  "yieldUnit": "un/porções/kg/litros",
  "preparationTimeMinutes": 0,
  "items": [
    { "name": "Nome do ingrediente", "quantity": 1.0, "unit": "g/ml/un/kg", "cost": 0.00 }
  ],
  "instructions": "Modo de preparo se houver ou vazio"
}
Regras:
1. "isRecipe" é false se for apenas compra de insumos soltos.
2. Nome da receita e lista de ingredientes são obrigatórios.
3. Retorne apenas o JSON.`;

  const parts: any[] = [{ text: `${instruction}\n\nPedido do operador: "${prompt || '(somente anexo)'}"` }];

  if (file) {
    const filePart = await buildFileAiPart(file);
    parts.push(filePart);
  }

  const aiRes = await generateContentWithCache({
    model: 'gemini-3.5-flash-lite',
    contents: [{ parts }],
    config: { generationConfig: { responseMimeType: 'application/json' }, temperature: 0.1 },
    skipCache: true
  });

  const parsed = parseAIJSON<any>(aiRes.text, null);
  if (!parsed || parsed.isRecipe !== true || !parsed.name?.trim()) return null;

  const rawItems = Array.isArray(parsed.items) ? parsed.items : [];
  const items = rawItems
    .map((it: any) => ({
      name: String(it?.name || '').trim(),
      quantity: Number(it?.quantity || 1) > 0 ? Number(it.quantity) : 1,
      unit: String(it?.unit || 'un').trim() || 'un',
      cost: Number(it?.cost || 0) > 0 ? Number(it.cost) : undefined
    }))
    .filter((it: any) => it.name.length > 0);

  if (items.length === 0) return null;

  const draft: RecipeDraft = {
    name: String(parsed.name).trim(),
    yieldAmount: Number(parsed.yieldAmount || 1) > 0 ? Number(parsed.yieldAmount) : 1,
    yieldUnit: parsed.yieldUnit ? String(parsed.yieldUnit).trim() : 'un',
    preparationTimeMinutes: Number(parsed.preparationTimeMinutes || 0) >= 0 ? Number(parsed.preparationTimeMinutes) : 0,
    items,
    instructions: parsed.instructions ? String(parsed.instructions).trim() : undefined
  };

  return {
    success: true,
    type: 'recipe_draft',
    toolName: 'receitas_preparar_ficha',
    recipeDraft: draft,
    message: 'Ficha técnica identificada. Confira os ingredientes e rendimento no Centro de Custos antes de gravar.'
  };
}

/* ========================================================================= */
/* DESPACHANTE CENTRAL DE COMANDOS                                          */
/* ========================================================================= */

export async function sendAgenticCommand(
  prompt: string,
  history?: Array<{ role: 'user' | 'model'; parts: Array<{ text: string }> }>
): Promise<AgenticCommandResponse> {
  // 1. Títulos Financeiros (Contas a Pagar & Receber)
  if (looksLikeFinancialIntent(prompt)) {
    try {
      const draftRes = await prepareFinancialDraft(prompt);
      if (draftRes) return draftRes;
    } catch (err) {
      console.warn('[AgenticCommandService] Falha ao preparar rascunho financeiro; seguindo fluxo padrão:', err);
    }
  }

  // 2. Catálogo de Serviços
  if (looksLikeServicesIntent(prompt) && !looksLikeFinancialIntent(prompt)) {
    try {
      const servicesRes = await prepareServicesDraft(prompt);
      if (servicesRes) return servicesRes;
    } catch (err) {
      console.warn('[AgenticCommandService] Falha ao preparar serviços; seguindo fluxo padrão:', err);
    }
  }

  // 3. CRM de Clientes
  if (looksLikeClientIntent(prompt)) {
    try {
      const clientRes = await prepareClientDraft(prompt);
      if (clientRes) return clientRes;
    } catch (err) {
      console.warn('[AgenticCommandService] Falha ao preparar rascunho de cliente; seguindo fluxo padrão:', err);
    }
  }

  // 4. Estoque & Lotes com Validade
  if (looksLikeInventoryIntent(prompt)) {
    try {
      const invRes = await prepareInventoryDraft(prompt);
      if (invRes) return invRes;
    } catch (err) {
      console.warn('[AgenticCommandService] Falha ao preparar rascunho de estoque; seguindo fluxo padrão:', err);
    }
  }

  // 5. Agenda & Atendimentos
  if (looksLikeAppointmentIntent(prompt)) {
    try {
      const apptRes = await prepareAppointmentDraft(prompt);
      if (apptRes) return apptRes;
    } catch (err) {
      console.warn('[AgenticCommandService] Falha ao preparar agendamento; seguindo fluxo padrão:', err);
    }
  }

  // 6. Ordens de Serviço
  if (looksLikeWorkOrderIntent(prompt)) {
    try {
      const woRes = await prepareWorkOrderDraft(prompt);
      if (woRes) return woRes;
    } catch (err) {
      console.warn('[AgenticCommandService] Falha ao preparar ordem de serviço; seguindo fluxo padrão:', err);
    }
  }

  // 7. Receitas & Fichas Técnicas
  if (looksLikeRecipeIntent(prompt)) {
    try {
      const recRes = await prepareRecipeDraft(prompt);
      if (recRes) return recRes;
    } catch (err) {
      console.warn('[AgenticCommandService] Falha ao preparar receita; seguindo fluxo padrão:', err);
    }
  }

  let backendUrl = import.meta.env.VITE_BACKEND_URL || '';
  if (backendUrl.includes('run.app')) {
    backendUrl = '';
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  } catch (err) {
    console.error('[AgenticCommandService] Erro ao obter token de sessão:', err);
  }

  try {
    const response = await fetch(`${backendUrl}/api/ai/command`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prompt, history: history || [] })
    });

    if (response.ok) {
      const jsonRes = await response.json();
      if (jsonRes.type === 'read_result' && (jsonRes.data === null || jsonRes.data === undefined)) {
        return await executeClientSideAgentFallback(prompt);
      }
      return jsonRes;
    }

    // Se o backend retornou erro (500, 503, 401, 404, 405), aciona o fallback inteligente integrado via cliente
    console.warn(`[AgenticCommandService] /api/ai/command retornou status ${response.status}. Ativando processamento agêntico resiliente via cliente.`);
    return await executeClientSideAgentFallback(prompt);

  } catch (err: any) {
    // Se houve erro de rede ou qualquer exceção capturada, executa via orquestrador local resiliente
    console.warn('[AgenticCommandService] Falha na rota do servidor. Executando via orquestrador local:', err?.message);
    return await executeClientSideAgentFallback(prompt);
  }
}

/**
 * Processa documento fiscal (PDF ou Foto/Imagem de cupom) diretamente na Omnibar Agêntica,
 * extraindo todos os insumos e gerando a proposta de lançamento no Centro de Custos.
 */
export async function processAgenticDocumentFile(file: File): Promise<AgenticCommandResponse> {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

  try {
    if (isPdf) {
      if (file.size > 4.5 * 1024 * 1024) {
        throw new Error('O arquivo PDF excede o limite de 4.5MB. Envie um arquivo menor ou uma foto da nota.');
      }

      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const res = (e.target?.result as string).split(',')[1];
          resolve(res);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      let extractedData: any = null;
      let shouldFallback = true;

      try {
        const { data, error } = await supabase.functions.invoke('process-pdf-invoice', {
          body: {
            pdfBase64: base64Data,
            filename: file.name,
            mimeType: 'application/pdf'
          }
        });

        if (!error && data && data.itens && Array.isArray(data.itens)) {
          extractedData = data;
          shouldFallback = false;
        }
      } catch (edgeErr) {
        console.warn('[AgenticCommandService] Edge Function indisponível para PDF. Executando fallback Gemini:', edgeErr);
      }

      if (!extractedData && shouldFallback) {
        const fallbackPrompt = `Você é o extrator fiscal inteligente do R-MAX preparado para a Reforma Tributária (LC 214/2025). Analise este documento fiscal (PDF ou DANFE) e extraia todos os insumos, matérias-primas e produtos com custo.
Retorne EXCLUSIVAMENTE um objeto JSON válido no formato:
{
  "itens": [
    {
      "descricao": "Nome legível do insumo",
      "ncm": "1006.20.00",
      "quantidade": 1.0,
      "unidade": "UN/KG/LT/CX",
      "valor_total": 0.00,
      "valor_cbs": 0.00,
      "categoria": "variável"
    }
  ]
}`;
        const parts = [
          { text: fallbackPrompt },
          { inlineData: { data: base64Data, mimeType: 'application/pdf' } }
        ];

        const aiRes = await generateContentWithCache({
          model: 'gemini-3.5-flash-lite',
          contents: [{ parts }],
          config: { generationConfig: { responseMimeType: 'application/json' } },
          skipCache: true
        });

        extractedData = parseAIJSON<any>(aiRes.text, null);
      }

      const rawItems = extractedData?.itens || [];
      if (!Array.isArray(rawItems) || rawItems.length === 0) {
        throw new Error('Nenhum insumo legível foi localizado no PDF. Verifique se o arquivo não está corrompido.');
      }

      const parsedItems = rawItems.slice(0, 500).map((it: any) => {
        const rawName = it.descricao || it.name || 'Insumo';
        const rawTotal = Number(it.valor_total || it.price || 0);
        const qty = Number(it.quantidade || it.amount || 1);
        const unit = (it.unidade || it.unit || 'un').toLowerCase();
        const ncm = it.ncm ? String(it.ncm).trim() : '';

        // Detecção de Rendimento e Diluição de Custo Inteligente
        let itemYield = 1.0;
        let itemYieldUnit = unit;
        const upper = rawName.toUpperCase();
        const matchKg = upper.match(/(\d+(?:[,\.]\d+)?)\s*KG\b/);
        const matchG = upper.match(/(\d+(?:[,\.]\d+)?)\s*G(?:R)?\b/);
        const matchL = upper.match(/(\d+(?:[,\.]\d+)?)\s*L(?:ITROS?)?\b/);
        const matchMl = upper.match(/(\d+(?:[,\.]\d+)?)\s*ML\b/);
        const matchUn = upper.match(/(\d+)\s*(?:UN|UNIDADES|PCT|CX)\b/);

        if (matchKg) {
          itemYield = parseFloat(matchKg[1].replace(',', '.'));
          itemYieldUnit = 'kg';
        } else if (matchG) {
          itemYield = parseFloat(matchG[1].replace(',', '.')) / 1000.0;
          itemYieldUnit = 'kg';
        } else if (matchL) {
          itemYield = parseFloat(matchL[1].replace(',', '.'));
          itemYieldUnit = 'l';
        } else if (matchMl) {
          itemYield = parseFloat(matchMl[1].replace(',', '.')) / 1000.0;
          itemYieldUnit = 'l';
        } else if (matchUn) {
          itemYield = parseInt(matchUn[1], 10);
          itemYieldUnit = 'un';
        }

        const isBuffet = (
          upper.includes('AÇAÍ') || upper.includes('ACAI') ||
          upper.includes('SORVETE') || upper.includes('SORV ') ||
          upper.includes('CREME DE ') || upper.includes('GELATO') ||
          upper.includes('BAIXA MANUAL')
        ) && !['PICOLÉ', 'PICOLE', 'PALETA', 'COPO', 'POTINHO'].some((k: string) => upper.includes(k));

        const stockControlMode = isBuffet ? 'pot_open' : 'unit_sale';

        // Inteligência da Reforma Tributária (LC 214/2025 & EC 132/2023):
        const taxCredits = calculateItemTaxCredits({
          grossTotal: rawTotal,
          ncm,
          description: rawName,
          cbsCreditExplicit: it.valor_cbs !== undefined ? Number(it.valor_cbs) : undefined,
          ibsCreditExplicit: it.valor_ibs !== undefined ? Number(it.valor_ibs) : undefined,
        });

        return {
          name: rawName,
          price: taxCredits.netTotal,
          grossPrice: Number(rawTotal.toFixed(2)),
          cbsCredit: taxCredits.cbsCredit,
          ibsCredit: taxCredits.ibsCredit,
          totalCredits: taxCredits.totalCredits,
          isCbna: taxCredits.isCbna,
          isReducao60: taxCredits.isReducao60,
          hasImpostoSeletivo: taxCredits.hasImpostoSeletivo,
          ncm,
          unit: itemYieldUnit || unit,
          quantity: qty,
          yieldAmount: itemYield > 0 ? (itemYield * qty) : qty,
          stockControlMode,
          salePrice: (it.preco_venda && Number(it.preco_venda) > 0.05) ? Number(it.preco_venda) : 0,
          category: (it.categoria === 'fixo' ? 'fixo' : 'variável')
        };
      });

      const totalGross = parsedItems.reduce((acc: number, curr: any) => acc + curr.grossPrice, 0);
      const totalNet = parsedItems.reduce((acc: number, curr: any) => acc + curr.price, 0);
      const totalCbsCredits = parsedItems.reduce((acc: number, curr: any) => acc + curr.cbsCredit, 0);
      const totalIbsCredits = parsedItems.reduce((acc: number, curr: any) => acc + curr.ibsCredit, 0);
      const totalRecoverableCredits = Number((totalCbsCredits + totalIbsCredits).toFixed(2));

      return {
        success: true,
        type: 'proposal',
        toolName: 'custos_cadastrar_lote_materiais',
        proposal: {
          actionId: `prop_pdf_${Date.now()}`,
          toolName: 'custos_cadastrar_lote_materiais',
          description: totalRecoverableCredits > 0
            ? `Lançamento inteligente de ${parsedItems.length} insumos com R$ ${totalRecoverableCredits.toFixed(2)} de créditos de CBS e IBS apurados (Reforma Tributária)`
            : `Lançamento de ${parsedItems.length} insumo(s) extraídos da nota fiscal (${file.name})`,
          parameters: {
            sourceFilename: file.name,
            totalGrossValue: totalGross,
            totalValue: totalNet,
            totalCbsCredits,
            totalIbsCredits,
            totalCredits: totalRecoverableCredits,
            items: parsedItems
          },
          items: parsedItems,
          totalValue: totalNet,
          diffSummary: parsedItems.slice(0, 6).map((it: any) => ({
            field: it.name,
            proposedValue: it.isCbna 
              ? `R$ ${it.price.toFixed(2)} (${it.quantity} ${it.unit} - Cesta Básica Alíquota Zero)`
              : it.isReducao60
                ? `R$ ${it.price.toFixed(2)} líquido (Redução 60% CBS/IBS - Créditos R$ ${it.totalCredits.toFixed(2)})`
                : it.hasImpostoSeletivo
                  ? `R$ ${it.price.toFixed(2)} líquido (Sujeito a Imposto Seletivo IS)`
                  : it.totalCredits > 0
                    ? `R$ ${it.price.toFixed(2)} líquido (Bruto R$ ${it.grossPrice.toFixed(2)} - Créditos CBS/IBS R$ ${it.totalCredits.toFixed(2)})`
                    : `R$ ${it.price.toFixed(2)} (${it.quantity} ${it.unit})`
          }))
        },
        message: totalRecoverableCredits > 0
          ? `Identificados ${parsedItems.length} insumos com R$ ${totalRecoverableCredits.toFixed(2)} em créditos recuperados (CBS: R$ ${totalCbsCredits.toFixed(2)} | IBS: R$ ${totalIbsCredits.toFixed(2)}). O custo líquido foi gravado para proteger suas receitas.`
          : `Foram identificados ${parsedItems.length} insumos no PDF da nota fiscal. Confira os valores e confirme para gravar no Centro de Custos.`
      };

    } else {
      // Processamento de Imagem (Cupom fiscal / foto)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('A imagem excede 5MB. Tire uma foto mais próxima ou reduza a resolução.');
      }

      const compressedDataUrl = await compressImage(file, { purpose: 'ocr' });
      const base64Data = compressedDataUrl.split(',')[1];

      const prompt = `Você é o extrator fiscal inteligente do R-MAX preparado para a Reforma Tributária (LC 214/2025). Analise a foto deste cupom fiscal ou recibo e retorne EXCLUSIVAMENTE um JSON estruturado:
{
  "inventory_items": [
    {
      "name": "Nome legível do insumo ou produto",
      "ncm": "1006.20.00",
      "price": 0.00,
      "cbs_credit": 0.00,
      "ibs_credit": 0.00,
      "unit": "un/kg/gr/ml/lt",
      "amount": 1.0,
      "category": "variável"
    }
  ]
}
Regras:
1. "price" é o valor final pago pelo item.
2. "amount" é o rendimento ou quantidade adquirida (ex: 500gr -> amount: 500, unit: gr).
3. "category" deve ser "variável" (ou "fixo" para ferramentas duráveis).
4. Retorne APENAS o JSON puro. Extraia no máximo até 50 itens.`;

      const parts = [
        { text: prompt },
        { inlineData: { data: base64Data, mimeType: 'image/jpeg' } }
      ];

      const aiRes = await generateContentWithCache({
        model: 'gemini-3.5-flash-lite',
        contents: [{ parts }],
        config: { generationConfig: { responseMimeType: 'application/json' } },
        skipCache: true
      });

      const extracted = parseAIJSON<any>(aiRes.text, null);
      const rawList = extracted?.inventory_items || [];

      if (!Array.isArray(rawList) || rawList.length === 0) {
        throw new Error('Nenhum item ou preço foi identificado nesta imagem. Tente tirar uma foto mais nítida.');
      }

      const parsedItems = rawList.slice(0, 50).map((it: any) => {
        const rawName = it.name || 'Item';
        const rawTotal = Number(it.price || 0);
        const qty = Number(it.amount || 1);
        const unit = (it.unit || 'un').toLowerCase();
        const ncm = it.ncm ? String(it.ncm).trim() : '';

        // Detecção de Rendimento e Diluição de Custo Inteligente
        let itemYield = 1.0;
        let itemYieldUnit = unit;
        const upper = rawName.toUpperCase();
        const matchKg = upper.match(/(\d+(?:[,\.]\d+)?)\s*KG\b/);
        const matchG = upper.match(/(\d+(?:[,\.]\d+)?)\s*G(?:R)?\b/);
        const matchL = upper.match(/(\d+(?:[,\.]\d+)?)\s*L(?:ITROS?)?\b/);
        const matchMl = upper.match(/(\d+(?:[,\.]\d+)?)\s*ML\b/);
        const matchUn = upper.match(/(\d+)\s*(?:UN|UNIDADES|PCT|CX)\b/);

        if (matchKg) {
          itemYield = parseFloat(matchKg[1].replace(',', '.'));
          itemYieldUnit = 'kg';
        } else if (matchG) {
          itemYield = parseFloat(matchG[1].replace(',', '.')) / 1000.0;
          itemYieldUnit = 'kg';
        } else if (matchL) {
          itemYield = parseFloat(matchL[1].replace(',', '.'));
          itemYieldUnit = 'l';
        } else if (matchMl) {
          itemYield = parseFloat(matchMl[1].replace(',', '.')) / 1000.0;
          itemYieldUnit = 'l';
        } else if (matchUn) {
          itemYield = parseInt(matchUn[1], 10);
          itemYieldUnit = 'un';
        }

        const isBuffet = (
          upper.includes('AÇAÍ') || upper.includes('ACAI') ||
          upper.includes('SORVETE') || upper.includes('SORV ') ||
          upper.includes('CREME DE ') || upper.includes('GELATO') ||
          upper.includes('BAIXA MANUAL')
        ) && !['PICOLÉ', 'PICOLE', 'PALETA', 'COPO', 'POTINHO'].some((k: string) => upper.includes(k));

        const stockControlMode = isBuffet ? 'pot_open' : 'unit_sale';

        // Inteligência da Reforma Tributária (LC 214/2025 & EC 132/2023):
        const taxCredits = calculateItemTaxCredits({
          grossTotal: rawTotal,
          ncm,
          description: rawName,
          cbsCreditExplicit: it.cbs_credit !== undefined ? Number(it.cbs_credit) : undefined,
          ibsCreditExplicit: it.ibs_credit !== undefined ? Number(it.ibs_credit) : undefined,
        });

        return {
          name: rawName,
          price: taxCredits.netTotal,
          grossPrice: Number(rawTotal.toFixed(2)),
          cbsCredit: taxCredits.cbsCredit,
          ibsCredit: taxCredits.ibsCredit,
          totalCredits: taxCredits.totalCredits,
          isCbna: taxCredits.isCbna,
          isReducao60: taxCredits.isReducao60,
          hasImpostoSeletivo: taxCredits.hasImpostoSeletivo,
          ncm,
          unit: itemYieldUnit || unit,
          quantity: qty,
          yieldAmount: itemYield > 0 ? (itemYield * qty) : qty,
          stockControlMode,
          salePrice: (it.preco_venda && Number(it.preco_venda) > 0.05) ? Number(it.preco_venda) : 0,
          category: (it.category === 'fixo' ? 'fixo' : 'variável')
        };
      });

      const totalGross = parsedItems.reduce((acc: number, curr: any) => acc + curr.grossPrice, 0);
      const totalNet = parsedItems.reduce((acc: number, curr: any) => acc + curr.price, 0);
      const totalCbsCredits = parsedItems.reduce((acc: number, curr: any) => acc + curr.cbsCredit, 0);
      const totalIbsCredits = parsedItems.reduce((acc: number, curr: any) => acc + curr.ibsCredit, 0);
      const totalRecoverableCredits = Number((totalCbsCredits + totalIbsCredits).toFixed(2));

      return {
        success: true,
        type: 'proposal',
        toolName: 'custos_cadastrar_lote_materiais',
        proposal: {
          actionId: `prop_img_${Date.now()}`,
          toolName: 'custos_cadastrar_lote_materiais',
          description: totalRecoverableCredits > 0
            ? `Lançamento inteligente de ${parsedItems.length} insumos com R$ ${totalRecoverableCredits.toFixed(2)} de créditos de CBS e IBS apurados (Reforma Tributária)`
            : `Lançamento de ${parsedItems.length} insumo(s) do cupom fiscal (${file.name})`,
          parameters: {
            sourceFilename: file.name,
            totalGrossValue: totalGross,
            totalValue: totalNet,
            totalCbsCredits,
            totalIbsCredits,
            totalCredits: totalRecoverableCredits,
            items: parsedItems
          },
          items: parsedItems,
          totalValue: totalNet,
          diffSummary: parsedItems.slice(0, 6).map((it: any) => ({
            field: it.name,
            proposedValue: it.isCbna 
              ? `R$ ${it.price.toFixed(2)} (${it.quantity} ${it.unit} - Cesta Básica Alíquota Zero)`
              : it.isReducao60
                ? `R$ ${it.price.toFixed(2)} líquido (Redução 60% CBS/IBS - Créditos R$ ${it.totalCredits.toFixed(2)})`
                : it.hasImpostoSeletivo
                  ? `R$ ${it.price.toFixed(2)} líquido (Sujeito a Imposto Seletivo IS)`
                  : it.totalCredits > 0
                    ? `R$ ${it.price.toFixed(2)} líquido (Bruto R$ ${it.grossPrice.toFixed(2)} - Créditos CBS/IBS R$ ${it.totalCredits.toFixed(2)})`
                    : `R$ ${it.price.toFixed(2)} (${it.quantity} ${it.unit})`
          }))
        },
        message: totalRecoverableCredits > 0
          ? `Identificados ${parsedItems.length} insumos com R$ ${totalRecoverableCredits.toFixed(2)} em créditos recuperados (CBS: R$ ${totalCbsCredits.toFixed(2)} | IBS: R$ ${totalIbsCredits.toFixed(2)}). O custo líquido foi gravado para proteger seu mark-up.`
          : `Foram identificados ${parsedItems.length} insumos na foto do cupom fiscal. Confira os valores e confirme para gravar no Centro de Custos.`
      };
    }
  } catch (err: any) {
    console.error('[Agentic Document Extraction Error]:', err);
    throw new Error(err.message || 'Falha ao analisar documento fiscal.');
  }
}

export async function confirmAgenticMutation(
  toolName: string,
  parameters: Record<string, any>
): Promise<{ success: boolean; message: string; data?: any }> {
  let backendUrl = import.meta.env.VITE_BACKEND_URL || '';
  if (backendUrl.includes('run.app')) {
    backendUrl = '';
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  } catch (err) {
    console.error('[AgenticCommandService] Erro ao obter token de sessão:', err);
  }

  try {
    const response = await fetch(`${backendUrl}/api/ai/command/confirm`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ toolName, parameters })
    });

    if (response.ok) {
      return await response.json();
    }

    if (response.status === 404 || response.status === 405) {
      console.warn(`[AgenticCommandService] /api/ai/command/confirm retornou ${response.status}. Executando mutação via Supabase client.`);
      return await executeClientSideMutationFallback(toolName, parameters);
    }

    const errorText = await response.text();
    let message = 'Erro ao executar alteração aprovada.';
    try {
      const parsed = JSON.parse(errorText);
      message = parsed.message || parsed.error || message;
    } catch {
      // Ignora erro de parse
    }
    throw new Error(message);

  } catch (err: any) {
    if (err.message && (err.message.includes('405') || err.message.includes('404') || err.message.includes('Failed to fetch'))) {
      return await executeClientSideMutationFallback(toolName, parameters);
    }
    throw err;
  }
}

/**
 * Fallback resiliente: processa o comando usando generateContentWithCache e executa consultas diretamente via Supabase client
 */
async function executeClientSideAgentFallback(
  prompt: string,
  userTier: 'free' | 'pro' | 'especialista' | 'empresa' = 'empresa'
): Promise<AgenticCommandResponse> {
  const { data: { user } } = await supabase.auth.getUser();
  const userId = user?.id || '';

  const systemInstruction = `Você é o Orquestrador Agêntico do sistema R-MAX (Precificação Inteligente & ERP).
O plano do usuário é "${userTier.toUpperCase()}".
Interprete a intenção do comando em linguagem natural e retorne estritamente um JSON no seguinte formato:
{
  "type": "read" | "write" | "navigation" | "text",
  "toolName": "nome_da_ferramenta_ou_null",
  "parameters": { "param": "valor" },
  "message": "Mensagem descritiva e técnica para o usuário",
  "content": "Resposta textual caso type seja 'text'"
}

Catálogo de ferramentas:
- custos_consultar_custo_hora (read) -> para saber o valor da hora de trabalho, valor hora, custo da hora técnica, taxa horária atual
- custos_atualizar_custo_hora (write, params: { novoValorHora: number }) -> reajuste da hora técnica de trabalho
- custos_consultar_despesas_fixas (read) -> despesas operacionais fixas, pró-labore, taxa horária base
- custos_cadastrar_material (write, params: { name: string, price: number, unit?: string, category?: 'variável' | 'fixo', quantity?: number }) -> compras de insumos, matérias-primas ou despesas expressas em texto (ex: "comprei 10kg de queijo por R$ 380", "paguei 150 reais de luz", "gastei 50 de embalagens", "cadastrar despesa fixa de aluguel 1200")
- custos_atualizar_preco_material (write, params: { nomeMaterial: string, novoPreco: number }) -> atualizar preço de item existente
- vendas_consultar_resumo (read, params: { periodo: 'mes_atual' }) -> faturamento, ticket médio, orçamentos aprovados
- vendas_listar_ultimas (read, params: { limite: 5 }) -> orçamentos emitidos recentes
- estoque_consultar_saldo (read, params: { nomeProduto: string }) -> saldo e estoque mínimo
- estoque_consultar_balancete (read) -> valor total imobilizado em mercadorias/insumos
- estoque_ajustar_quantidade (write, params: { nomeProduto: string, quantidade: number, tipoMovimento: string })
- pdv_consultar_fechamento_caixa (read, params: { dataReferencia: 'hoje' }) -> total vendido hoje por forma de pagamento
- crm_buscar_cliente (read, params: { termo: string }) -> busca de cliente por nome ou contato
- crm_cadastrar_cliente (write, params: { name: string, phone?: string, document?: string, address?: string, notes?: string }) -> cadastro de novo cliente
- agenda_criar_agendamento (write, params: { clientName: string, serviceName: string, scheduledDate: string, startTime: string })
- agenda_consultar_disponibilidade (read, params: { date: 'hoje' | string }) -> compromissos e horários agendados
- os_criar_ordem_servico (write, params: { clientName: string, title: string, notes?: string })
- sistema_navegar_para (navigation, params: { view: string, label: string }) -> direcionamento rápido de tela. Views válidas:
  * "speed-pos" (Sorveteria / Buffet / PDV Rápido)
  * "market-radar" (Radar de Mercado / Preços da Região / Preços da Concorrência / Pesquisa de Mercado)
  * "costs" (Centro de Custos e Insumos)
  * "appointments" (Agenda de Atendimentos)
  * "clients" (CRM de Clientes)
  * "quotes" (Orçamentos)
  * "work-orders" (Ordens de Serviço)
  * "inventory" (Estoque e Lotes)
  * "erp-hub" (Painel Geral do ERP)

DIRETRIZES:
1. Tom 100% sério, técnico, conciso e operacional (Google Material 3).
2. NUNCA use estrelinhas, emojis decorativos ou jargões como "mágico".
3. Se o usuário perguntar sobre "valor hora", "meu valor hora", "custo da hora", "quanto está minha hora", use ESTRITAMENTE custos_consultar_custo_hora. NUNCA use agenda para valores monetários.
4. Use agenda_consultar_disponibilidade APENAS quando o usuário perguntar sobre horários marcados ou compromissos ("agenda de hoje", "atendimentos", "quem está agendado").
5. Se o usuário estiver citando um gasto/compra (ex: "comprei...", "gastei...", "paguei..."), use custos_cadastrar_material.
6. Se o usuário pedir para ir para uma tela ou ver preços da concorrência/mercado ("ir para...", "abrir...", "ver preços médios da concorrência", "concorrência na região"), use sistema_navegar_para.
7. Se for uma dúvida conceitual de gestão ou precificação, use type 'text' e explique de forma direta.`;

  try {
    const rawAi = await generateContentWithCache({
      model: 'gemini-3.5-flash-lite',
      contents: [
        {
          role: 'user',
          parts: [{ text: `${systemInstruction}\n\nComando do operador: "${prompt}"` }]
        }
      ],
      config: { responseMimeType: 'application/json', temperature: 0.1 }
    });

    const rawText = (typeof rawAi === 'string' ? rawAi : rawAi?.text) || '';

    let parsed: any = {};
    try {
      let cleaned = rawText.trim();
      if (cleaned.startsWith('```')) {
        cleaned = cleaned.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
      }
      parsed = JSON.parse(cleaned);
    } catch {
      return { success: true, userTier, type: 'text', content: rawText };
    }

    // Se for ação de navegação imediata
    if (parsed.type === 'navigation' && parsed.parameters?.view) {
      return {
        success: true,
        userTier,
        type: 'navigation',
        toolName: 'sistema_navegar_para',
        navigationTarget: {
          view: parsed.parameters.view,
          label: parsed.parameters.label || parsed.parameters.view
        },
        message: parsed.message || `Navegando para ${parsed.parameters.label || parsed.parameters.view}...`
      };
    }

    // Se for ação de alteração (write), monta proposta com diff
    if (parsed.type === 'write' && parsed.toolName) {
      const actionId = `prop_${Date.now()}`;
      
      // Proposta especial para cadastrar insumo/custo
      if (parsed.toolName === 'custos_cadastrar_material') {
        const itemPrice = Number(parsed.parameters?.price || 0);
        const itemQty = Number(parsed.parameters?.quantity || 1);
        const itemUnit = parsed.parameters?.unit || 'un';
        const itemName = parsed.parameters?.name || 'Insumo';
        const itemCategory = parsed.parameters?.category || 'variável';

        return {
          success: true,
          userTier,
          type: 'proposal',
          toolName: parsed.toolName,
          proposal: {
            actionId,
            toolName: parsed.toolName,
            description: `Lançar custo: ${itemName} a R$ ${itemPrice.toFixed(2)} (${itemQty} ${itemUnit})`,
            parameters: parsed.parameters || {},
            diffSummary: [
              { field: 'Item / Insumo', proposedValue: itemName },
              { field: 'Valor Pago', proposedValue: `R$ ${itemPrice.toFixed(2)}` },
              { field: 'Quantidade', proposedValue: `${itemQty} ${itemUnit}` },
              { field: 'Categoria', proposedValue: itemCategory === 'fixo' ? 'Custo Fixo' : 'Insumo Variável' }
            ]
          },
          message: parsed.message || `Proposta para lançar "${itemName}" no Centro de Custos.`
        };
      }

      return {
        success: true,
        userTier,
        type: 'proposal',
        toolName: parsed.toolName,
        proposal: {
          actionId,
          toolName: parsed.toolName,
          description: parsed.message || `Alteração em ${parsed.toolName}`,
          parameters: parsed.parameters || {},
          diffSummary: Object.entries(parsed.parameters || {}).map(([key, val]) => ({
            field: key,
            proposedValue: String(val)
          }))
        },
        message: parsed.message || 'Proposta de alteração gerada com sucesso.'
      };
    }

    // Se for ação de consulta (read), executa via Supabase
    if (parsed.type === 'read' && parsed.toolName && userId) {
      const tool = parsed.toolName;
      const params = parsed.parameters || {};

      if (tool === 'custos_consultar_custo_hora') {
        const { data: profile } = await supabase
          .from('user_business_profiles')
          .select('base_hour_value, weekly_work_hours, monthly_fixed_costs, pro_labore_goal')
          .eq('user_id', userId)
          .maybeSingle();

        const hora = Number(profile?.base_hour_value || 0);
        return {
          success: true,
          userTier,
          type: 'read_result',
          toolName: 'custos_consultar_custo_hora',
          data: {
            valorHoraAtual: `R$ ${hora.toFixed(2)} / hora`,
            jornadaSemanal: `${profile?.weekly_work_hours || 40} horas/semana`,
            metaProLabore: profile?.pro_labore_goal ? `R$ ${Number(profile.pro_labore_goal).toFixed(2)}/mês` : 'Não definida'
          },
          message: hora > 0
            ? `Seu valor da hora técnica de trabalho atual é R$ ${hora.toFixed(2)}/hora.`
            : 'Seu custo da hora técnica ainda não foi configurado.'
        };
      }

      if (tool === 'custos_consultar_despesas_fixas') {
        const { data: profile } = await supabase
          .from('user_business_profiles')
          .select('monthly_fixed_costs, pro_labore_goal, weekly_work_hours, base_hour_value')
          .eq('user_id', userId)
          .maybeSingle();

        const { data: materials } = await supabase
          .from('materials')
          .select('name, price, unit')
          .eq('user_id', userId)
          .limit(8);

        return {
          success: true,
          userTier,
          type: 'read_result',
          toolName: tool,
          data: {
            despesasFixasMensais: profile?.monthly_fixed_costs || 0,
            metaProLabore: profile?.pro_labore_goal || 0,
            custoHoraBase: profile?.base_hour_value || 0,
            insumosCadastrados: materials || []
          },
          message: 'Composição de despesas fixas e custo hora obtida com sucesso.'
        };
      }

      if (tool === 'vendas_consultar_resumo') {
        const { data: quotes } = await supabase
          .from('quotes')
          .select('final_price, status, created_at')
          .eq('user_id', userId);

        const aprovados = (quotes || []).filter((q: any) => q.status === 'approved' || q.status === 'completed');
        const faturado = aprovados.reduce((acc: number, q: any) => acc + Number(q.final_price || 0), 0);

        return {
          success: true,
          userTier,
          type: 'read_result',
          toolName: tool,
          data: {
            totalOrcamentosEmitidos: quotes?.length || 0,
            orcamentosAprovados: aprovados.length,
            totalFaturadoReais: faturado,
            ticketMedioReais: aprovados.length ? Math.round((faturado / aprovados.length) * 100) / 100 : 0
          },
          message: 'Resumo de faturamento e vendas calculado com sucesso.'
        };
      }

      if (tool === 'estoque_consultar_saldo' || tool === 'estoque_consultar_balancete') {
        let q = supabase.from('materials').select('name, price, stockquantity, unit').eq('user_id', userId);
        if (params.nomeProduto) q = q.ilike('name', `%${params.nomeProduto}%`);
        const { data } = await q.limit(10);

        let totalEstoque = 0;
        (data || []).forEach((item: any) => {
          totalEstoque += (Number(item.stockquantity || 0) * Number(item.price || 0));
        });

        return {
          success: true,
          userTier,
          type: 'read_result',
          toolName: tool,
          data: {
            itens: data || [],
            valorTotalEstoqueReais: Math.round(totalEstoque * 100) / 100
          },
          message: data?.length ? `Encontrado(s) ${data.length} registro(s) de estoque.` : 'Nenhum insumo localizado.'
        };
      }

      if (tool === 'pdv_consultar_fechamento_caixa') {
        const todayIso = getLocalDateString();
        const { data } = await supabase
          .from('receipts')
          .select('amount_paid, payment_method')
          .eq('user_id', userId)
          .gte('created_at', `${todayIso}T00:00:00`);

        const total = (data || []).reduce((acc: number, r: any) => acc + Number(r.amount_paid || 0), 0);
        return {
          success: true,
          userTier,
          type: 'read_result',
          toolName: tool,
          data: { totalVendasHojeReais: total, totalOperacoes: data?.length || 0 },
          message: `Fechamento do caixa hoje apurado: R$ ${total.toFixed(2)}.`
        };
      }

      if (tool === 'crm_buscar_cliente') {
        const { data } = await supabase
          .from('clients')
          .select('id, name, phone, document')
          .eq('user_id', userId)
          .ilike('name', `%${params.termo || ''}%`)
          .limit(10);

        return {
          success: true,
          userTier,
          type: 'read_result',
          toolName: tool,
          data,
          message: data?.length ? `Encontrado(s) ${data.length} cliente(s).` : 'Nenhum cliente localizado.'
        };
      }

      if (tool === 'agenda_consultar_disponibilidade') {
        const todayIso = getLocalDateString();
        let queryDate = todayIso;
        if (params.date && params.date !== 'hoje') {
          const rawD = String(params.date).toLowerCase().trim();
          if (rawD === 'amanhã' || rawD === 'amanha') {
            const tmr = new Date(Date.now() + 24 * 60 * 60 * 1000);
            queryDate = new Intl.DateTimeFormat('en-CA', {
              timeZone: 'America/Sao_Paulo',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }).format(tmr);
          } else if (rawD === 'ontem') {
            const yst = new Date(Date.now() - 24 * 60 * 60 * 1000);
            queryDate = new Intl.DateTimeFormat('en-CA', {
              timeZone: 'America/Sao_Paulo',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }).format(yst);
          } else if (rawD.includes('/')) {
            const parts = rawD.split('/');
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2] || todayIso.split('-')[0];
            queryDate = `${year}-${month}-${day}`;
          } else if (/^\d{4}-\d{2}-\d{2}$/.test(rawD)) {
            queryDate = rawD;
          }
        }

        const { data } = await supabase
          .from('appointments')
          .select('id, client_name, service_name, scheduled_time, status')
          .eq('user_id', userId)
          .eq('scheduled_date', queryDate)
          .order('scheduled_time', { ascending: true })
          .limit(15);

        return {
          success: true,
          userTier,
          type: 'read_result',
          toolName: tool,
          data,
          message: data?.length ? `${data.length} atendimento(s) agendado(s) para ${queryDate}.` : `Nenhum agendamento para ${queryDate}. Agenda livre!`
        };
      }
    }

    return {
      success: true,
      userTier,
      type: 'text',
      content: parsed.content || parsed.message || rawText
    };

  } catch (aiErr: any) {
    console.warn('[Agentic Fallback AI Warning]:', aiErr?.message);
    // Heurística de resiliência caso o serviço de IA esteja indisponível:
    const lowerPrompt = prompt.toLowerCase();
    if (/(?:valor|custo|taxa|pre[çc]o).*(?:hora)|quanto.*(?:est[áa]|t[áa]).*hora/i.test(lowerPrompt)) {
      const { data: profile } = await supabase
        .from('user_business_profiles')
        .select('base_hour_value, weekly_work_hours, monthly_fixed_costs, pro_labore_goal')
        .eq('user_id', userId)
        .maybeSingle();

      const hora = Number(profile?.base_hour_value || 0);
      return {
        success: true,
        userTier,
        type: 'read_result',
        toolName: 'custos_consultar_custo_hora',
        data: {
          valorHoraAtual: `R$ ${hora.toFixed(2)} / hora`,
          jornadaSemanal: `${profile?.weekly_work_hours || 40} horas/semana`,
          metaProLabore: profile?.pro_labore_goal ? `R$ ${Number(profile.pro_labore_goal).toFixed(2)}/mês` : 'Não definida'
        },
        message: hora > 0
          ? `Seu valor da hora técnica de trabalho atual é R$ ${hora.toFixed(2)}/hora.`
          : 'Seu custo da hora técnica ainda não foi configurado.'
      };
    }
    throw new Error('Serviço de IA temporariamente indisponível. Tente novamente em alguns instantes.');
  }
}

/**
 * Fallback de mutação local para efetivar alterações no Supabase caso o endpoint /api/ai/command/confirm não responda
 */
async function executeClientSideMutationFallback(
  toolName: string,
  parameters: Record<string, any>
): Promise<{ success: boolean; message: string; data?: any }> {
  const { data: { user } } = await supabase.auth.getUser();
  const userId = user?.id || '';

  if (!userId) {
    throw new Error('Sessão de usuário não identificada para gravar alterações.');
  }

  // Mutação em lote: Lançamento de nota fiscal / cupom com vários insumos
  if (toolName === 'custos_cadastrar_lote_materiais' && Array.isArray(parameters.items)) {
    const materialsToInsert = parameters.items.map((item: any) => ({
      user_id: userId,
      name: item.name,
      price: Number(item.price || 0),
      unit: item.unit || 'un',
      category: item.category === 'fixo' ? 'fixo' : 'variável',
      yield_amount: Number(item.yieldAmount || item.quantity || 1),
      stockquantity: Number(item.quantity || 0),
      sale_price: Number(item.salePrice || 0),
      stock_control_mode: item.stockControlMode || 'unit_sale',
      created_at: new Date().toISOString()
    }));

    const { data, error } = await supabase.from('materials').insert(materialsToInsert).select();
    if (error) throw error;
    return {
      success: true,
      message: `${materialsToInsert.length} insumo(s) cadastrado(s) com sucesso no Centro de Custos!`,
      data
    };
  }

  // Lançamento avulso de insumo / compra de insumo
  if (toolName === 'custos_cadastrar_material') {
    const { data, error } = await supabase.from('materials').insert({
      user_id: userId,
      name: parameters.name,
      price: Number(parameters.price || 0),
      unit: parameters.unit || 'un',
      category: parameters.category === 'fixo' ? 'fixo' : 'variável',
      yield_amount: Number(parameters.quantity || 1),
      stockquantity: Number(parameters.quantity || 0),
      created_at: new Date().toISOString()
    }).select().single();
    if (error) throw error;
    return { success: true, message: `Insumo "${parameters.name}" cadastrado no Centro de Custos!`, data };
  }

  if (toolName === 'crm_cadastrar_cliente') {
    const { data, error } = await supabase.from('clients').insert({
      user_id: userId,
      name: parameters.name,
      phone: parameters.phone || null,
      document: parameters.document || null,
      address: parameters.address || null,
      notes: parameters.notes || null
    }).select().single();
    if (error) throw error;
    return { success: true, message: `Cliente "${parameters.name}" cadastrado com sucesso!`, data };
  }

  if (toolName === 'custos_atualizar_custo_hora') {
    const novoValor = Number(parameters.novoValorHora);
    const { data, error } = await supabase.from('user_business_profiles').upsert(
      { user_id: userId, base_hour_value: novoValor },
      { onConflict: 'user_id' }
    ).select().single();
    if (error) throw error;
    return { success: true, message: `Custo da hora técnica atualizado para R$ ${novoValor.toFixed(2)}!`, data };
  }

  if (toolName === 'agenda_criar_agendamento') {
    const { data, error } = await supabase.from('appointments').insert({
      user_id: userId,
      client_name: parameters.clientName,
      service_name: parameters.serviceName,
      scheduled_date: parameters.scheduledDate,
      scheduled_time: parameters.startTime || parameters.scheduledTime || '09:00:00',
      status: 'scheduled'
    }).select().single();
    if (error) throw error;
    return { success: true, message: `Agendamento criado para ${parameters.clientName} em ${parameters.scheduledDate}!`, data };
  }

  if (toolName === 'os_criar_ordem_servico') {
    const orderNum = `OS-${Date.now().toString().slice(-6)}`;
    const { data, error } = await supabase.from('work_orders').insert({
      user_id: userId,
      order_number: orderNum,
      client_name: parameters.clientName,
      title: parameters.title,
      notes: parameters.notes || null,
      status: 'open'
    }).select().single();
    if (error) throw error;
    return { success: true, message: `Ordem de Serviço "${parameters.title}" (${orderNum}) criada com sucesso!`, data };
  }

  if (toolName === 'custos_atualizar_preco_material') {
    const novoPreco = Number(parameters.novoPreco);
    let q = supabase.from('materials').update({ price: novoPreco }).eq('user_id', userId);
    if (parameters.materialId) {
      q = q.eq('id', parameters.materialId);
    } else if (parameters.nomeMaterial) {
      q = q.ilike('name', `%${parameters.nomeMaterial}%`);
    }
    const { data, error } = await q.select();
    if (error) throw error;
    return { success: true, message: `Preço do material reajustado para R$ ${novoPreco.toFixed(2)}!`, data };
  }

  if (toolName === 'estoque_ajustar_quantidade') {
    const qtd = Number(parameters.quantidade || 0);
    let q = supabase.from('materials').update({ stockquantity: qtd }).eq('user_id', userId);
    if (parameters.nomeProduto) {
      q = q.ilike('name', `%${parameters.nomeProduto}%`);
    }
    const { data, error } = await q.select();
    if (error) throw error;
    return { success: true, message: `Estoque de "${parameters.nomeProduto}" ajustado para ${qtd}!`, data };
  }

  return { success: true, message: 'Alteração aplicada com sucesso no sistema!' };
}
